import React, { useCallback, useEffect, useRef, useState } from 'react';
import Icon from '../../design-system/primitives/Icon';
import { Button } from '../ui/button';
import { apiService } from '../../services/api';

const mono = { fontFamily: 'var(--font-mono)', letterSpacing: '-0.01em' };

const fmtRows = (n) => (n == null ? '' : `${Number(n).toLocaleString()} rows`);

/**
 * Browse a verified warehouse connection: schema list → table list → preview,
 * plus a free-form SQL tab. "Import" lands the selection in the analysis as a
 * raw dataset (the parent handles what happens after).
 */
const WarehouseBrowser = ({ connection, analysisId, onImported, toast }) => {
  const [schemas, setSchemas] = useState(null);
  const [schema, setSchema] = useState(null);
  const [tables, setTables] = useState(null);
  const [table, setTable] = useState(null);
  const [preview, setPreview] = useState(null);
  const [mode, setMode] = useState('tables'); // 'tables' | 'sql'
  const [sqlText, setSqlText] = useState('');
  const [rowLimit, setRowLimit] = useState('');
  const [loading, setLoading] = useState(false);
  const [previewLoading, setPreviewLoading] = useState(false);
  const [importing, setImporting] = useState(false);
  const [error, setError] = useState(null);

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    setError(null);
    apiService.getConnectionSchemas(connection.id)
      .then((resp) => { if (!cancelled) setSchemas(resp.data.schemas || []); })
      .catch((e) => { if (!cancelled) setError(e?.response?.data?.detail || 'Could not list schemas.'); })
      .finally(() => { if (!cancelled) setLoading(false); });
    return () => { cancelled = true; };
  }, [connection.id]);

  const openSchema = useCallback((name) => {
    setSchema(name);
    setTable(null);
    setPreview(null);
    setTables(null);
    setError(null);
    apiService.getConnectionTables(connection.id, name)
      .then((resp) => setTables(resp.data.tables || []))
      .catch((e) => setError(e?.response?.data?.detail || 'Could not list tables.'));
  }, [connection.id]);

  // Monotonic request id: preview queries resolve out of order (a slow big-table
  // preview can land after a fast one), so only the LATEST request may write
  // state — otherwise the grid shows table A's rows under table B's highlight.
  const previewSeq = useRef(0);

  const openPreview = useCallback((body) => {
    const seq = ++previewSeq.current;
    setPreviewLoading(true);
    setPreview(null);
    setError(null);
    apiService.previewWarehouse(connection.id, body)
      .then((resp) => { if (seq === previewSeq.current) setPreview(resp.data); })
      .catch((e) => { if (seq === previewSeq.current) setError(e?.response?.data?.detail || 'Preview failed.'); })
      .finally(() => { if (seq === previewSeq.current) setPreviewLoading(false); });
  }, [connection.id]);

  const openTable = useCallback((t) => {
    setTable(t);
    openPreview({ source: { schema: t.schema, table: t.name } });
  }, [openPreview]);

  const runSqlPreview = useCallback(() => {
    if (!sqlText.trim()) return;
    setTable(null);
    openPreview({ sql: sqlText });
  }, [sqlText, openPreview]);

  const handleImport = useCallback(async () => {
    if (importing) return;
    const body = { connection_id: connection.id };
    if (mode === 'sql' && sqlText.trim()) {
      body.sql = sqlText;
    } else if (table) {
      body.source = { schema: table.schema, table: table.name };
    } else {
      return;
    }
    const limit = parseInt(rowLimit, 10);
    if (!Number.isNaN(limit) && limit > 0) body.row_limit = limit;

    setImporting(true);
    setError(null);
    try {
      const resp = await apiService.importFromWarehouse(analysisId, body);
      onImported(resp.data, {
        analysisId,
        connectionName: connection.name,
        label: mode === 'sql' ? 'query result' : `${table.schema}.${table.name}`,
      });
    } catch (e) {
      const detail = e?.response?.data?.detail || 'Import failed.';
      setError(detail);
      if (toast) toast(detail, 'error');
    } finally {
      setImporting(false);
    }
  }, [importing, connection, analysisId, mode, sqlText, table, rowLimit, onImported, toast]);

  const canImport = !importing && ((mode === 'sql' && sqlText.trim()) || (mode === 'tables' && table));

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 14, minHeight: 0, flex: 1 }}>
      {/* Mode tabs */}
      <div style={{ display: 'flex', gap: 6 }}>
        {[['tables', 'Browse tables'], ['sql', 'SQL query']].map(([key, label]) => (
          <button
            key={key}
            onClick={() => { setMode(key); setPreview(null); setError(null); }}
            style={{
              padding: '7px 14px', borderRadius: 9, fontSize: 13, fontWeight: 500, cursor: 'pointer',
              background: mode === key ? 'var(--accent-soft)' : 'transparent',
              border: `1px solid ${mode === key ? 'var(--accent-soft-2)' : 'var(--line-soft)'}`,
              color: mode === key ? 'var(--accent)' : 'var(--fg-muted)',
            }}
          >
            {label}
          </button>
        ))}
      </div>

      {mode === 'tables' ? (
        <div style={{ display: 'flex', gap: 14, minHeight: 0, flex: 1 }}>
          {/* Schema / table tree */}
          <div style={{ width: 260, flexShrink: 0, overflowY: 'auto', border: '1px solid var(--line-soft)', borderRadius: 12, padding: 8 }}>
            {loading && <div style={{ padding: 12, fontSize: 13, color: 'var(--fg-subtle)' }}>Loading schemas…</div>}
            {schemas && schemas.length === 0 && <div style={{ padding: 12, fontSize: 13, color: 'var(--fg-subtle)' }}>No schemas visible.</div>}
            {(schemas || []).map((name) => (
              <div key={name}>
                <button
                  onClick={() => (schema === name ? setSchema(null) : openSchema(name))}
                  style={{
                    display: 'flex', alignItems: 'center', gap: 8, width: '100%', textAlign: 'left',
                    padding: '8px 10px', borderRadius: 8, border: 'none', cursor: 'pointer',
                    background: schema === name ? 'var(--bg-elev-2)' : 'transparent',
                    color: 'var(--fg)', fontSize: 13, ...mono,
                  }}
                >
                  <Icon name={schema === name ? 'chevron-down' : 'chevron-right'} size={13} color="var(--fg-subtle)" />
                  <Icon name="folder" size={13} color="var(--accent)" />
                  <span style={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{name}</span>
                </button>
                {schema === name && (
                  <div style={{ paddingLeft: 22 }}>
                    {tables === null && <div style={{ padding: '6px 10px', fontSize: 12, color: 'var(--fg-subtle)' }}>Loading…</div>}
                    {(tables || []).map((t) => (
                      <button
                        key={t.name}
                        onClick={() => openTable(t)}
                        style={{
                          display: 'flex', alignItems: 'center', gap: 8, width: '100%', textAlign: 'left',
                          padding: '6px 10px', borderRadius: 8, border: 'none', cursor: 'pointer',
                          background: table?.name === t.name ? 'var(--accent-soft)' : 'transparent',
                          color: table?.name === t.name ? 'var(--accent)' : 'var(--fg-muted)', fontSize: 13, ...mono,
                        }}
                        title={fmtRows(t.rows)}
                      >
                        <Icon name={t.type === 'view' ? 'eye' : 'table-2'} size={12} />
                        <span style={{ flex: 1, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{t.name}</span>
                        {t.rows != null && <span style={{ fontSize: 11, color: 'var(--fg-subtle)' }}>{Number(t.rows).toLocaleString()}</span>}
                      </button>
                    ))}
                  </div>
                )}
              </div>
            ))}
          </div>

          {/* Preview pane */}
          <PreviewPane preview={preview} loading={previewLoading} error={error} placeholder="Pick a table to preview the first rows." />
        </div>
      ) : (
        <div style={{ display: 'flex', flexDirection: 'column', gap: 10, minHeight: 0, flex: 1 }}>
          <textarea
            value={sqlText}
            onChange={(e) => setSqlText(e.target.value)}
            placeholder={`SELECT ... — read-only, a row limit is always applied (${connection.connection_type} SQL)`}
            rows={5}
            spellCheck={false}
            style={{
              width: '100%', resize: 'vertical', padding: '12px 14px',
              background: 'var(--bg-elev-2)', border: '1px solid var(--line-soft)', borderRadius: 12,
              color: 'var(--fg-strong)', fontSize: 13, outline: 'none', ...mono,
            }}
          />
          <div>
            <button
              onClick={runSqlPreview}
              disabled={!sqlText.trim() || previewLoading}
              style={{
                padding: '8px 16px', borderRadius: 9, fontSize: 13, fontWeight: 500,
                background: 'var(--bg-elev-2)', border: '1px solid var(--line)', color: 'var(--fg)',
                cursor: sqlText.trim() ? 'pointer' : 'default',
              }}
            >
              {previewLoading ? 'Running…' : 'Preview'}
            </button>
          </div>
          <PreviewPane preview={preview} loading={previewLoading} error={error} placeholder="Write a SELECT and preview it." />
        </div>
      )}

      {/* Import bar */}
      <div style={{ display: 'flex', alignItems: 'center', gap: 12, borderTop: '1px solid var(--line-soft)', paddingTop: 14 }}>
        <span style={{ fontSize: 12, color: 'var(--fg-subtle)' }}>Row limit</span>
        <input
          value={rowLimit}
          onChange={(e) => setRowLimit(e.target.value.replace(/[^0-9]/g, ''))}
          placeholder="all (capped)"
          style={{
            width: 110, padding: '7px 10px', borderRadius: 8, fontSize: 13,
            background: 'var(--bg-elev-2)', border: '1px solid var(--line-soft)', color: 'var(--fg)', outline: 'none', ...mono,
          }}
        />
        <div style={{ flex: 1 }} />
        <Button onClick={handleImport} disabled={!canImport || importing}>
          {importing
            ? <Icon name="loader-2" size={14} style={{ animation: 'qSpin 800ms linear infinite' }} />
            : <Icon name="download" size={14} />}
          {importing ? 'Importing…' : 'Import into analysis'}
        </Button>
      </div>
    </div>
  );
};

const PreviewPane = ({ preview, loading, error, placeholder }) => (
  <div style={{ flex: 1, minWidth: 0, minHeight: 160, border: '1px solid var(--line-soft)', borderRadius: 12, overflow: 'auto', position: 'relative' }}>
    {loading && <div style={{ padding: 16, fontSize: 13, color: 'var(--fg-subtle)' }}>Loading preview…</div>}
    {!loading && error && <div style={{ padding: 16, fontSize: 13, color: 'var(--red, #d5484f)' }}>{error}</div>}
    {!loading && !error && !preview && <div style={{ padding: 16, fontSize: 13, color: 'var(--fg-subtle)' }}>{placeholder}</div>}
    {!loading && !error && preview && (
      <table style={{ borderCollapse: 'collapse', fontSize: 12, width: '100%', ...mono }}>
        <thead>
          <tr>
            {preview.columns.map((c) => (
              <th key={c} style={{
                position: 'sticky', top: 0, background: 'var(--bg-elev-2)', textAlign: 'left',
                padding: '8px 12px', borderBottom: '1px solid var(--line)', color: 'var(--fg-strong)', whiteSpace: 'nowrap',
              }}>
                {c}
                <div style={{ fontWeight: 400, color: 'var(--fg-subtle)', fontSize: 10 }}>{preview.dtypes?.[c]}</div>
              </th>
            ))}
          </tr>
        </thead>
        <tbody>
          {preview.rows.map((row, i) => (
            <tr key={i}>
              {row.map((v, j) => (
                <td key={j} style={{ padding: '6px 12px', borderBottom: '1px solid var(--line-soft)', color: 'var(--fg-muted)', whiteSpace: 'nowrap', maxWidth: 240, overflow: 'hidden', textOverflow: 'ellipsis' }}>
                  {v == null ? <span style={{ color: 'var(--fg-subtle)' }}>∅</span> : String(v)}
                </td>
              ))}
            </tr>
          ))}
        </tbody>
      </table>
    )}
  </div>
);

export default WarehouseBrowser;
